import { useState, useEffect, useCallback, createContext, useContext, type ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export type SkillLevel = 'beginner' | 'intermediate' | 'advanced';

interface SportSkillLevels {
  [sport: string]: SkillLevel;
}

interface SportSkillContextType {
  levels: SportSkillLevels;
  loaded: boolean;
  getLevel: (sport: string) => SkillLevel | null;
  setLevel: (sport: string, level: SkillLevel) => Promise<void>;
}

const SportSkillContext = createContext<SportSkillContextType>({
  levels: {},
  loaded: false,
  getLevel: () => null,
  setLevel: async () => {},
});

export function SportSkillProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [levels, setLevels] = useState<SportSkillLevels>({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!user) {
      setLevels({});
      setLoaded(false);
      return;
    }

    supabase
      .from('profiles')
      .select('sport_skill_levels')
      .eq('user_id', user.id)
      .single()
      .then(({ data }) => {
        if (data && (data as any).sport_skill_levels) {
          setLevels((data as any).sport_skill_levels as SportSkillLevels);
        }
        setLoaded(true);
      });
  }, [user]);

  const getLevel = useCallback((sport: string): SkillLevel | null => {
    return levels[sport] ?? null;
  }, [levels]);

  const setLevel = useCallback(async (sport: string, level: SkillLevel) => {
    if (!user) return;
    const updated = { ...levels, [sport]: level };
    setLevels(updated);
    await supabase
      .from('profiles')
      .update({ sport_skill_levels: updated } as any)
      .eq('user_id', user.id);
  }, [user, levels]);

  return (
    <SportSkillContext.Provider value={{ levels, loaded, getLevel, setLevel }}>
      {children}
    </SportSkillContext.Provider>
  );
}

export function useSportSkillLevel() {
  return useContext(SportSkillContext);
}
